import { useFragment } from "@/generated";
import {
  DuplicateSlideDocument,
  type SlideFieldsFragment,
  SlideFieldsFragmentDoc,
} from "@/generated/graphql";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import { withToast } from "@/lib/utils/withToast";
import type { ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";

/**
 * Hook for duplicating a slide and updating the Apollo cache.

 *
 * @returns An object containing:
 *   - duplicateSlide: Async function to duplicate a slide. Accepts the slide ID, target lesson ID, new name,
 *     duplication type ("unique" | "linked"), and optionally the original slide ID. Returns the duplicated slide.
 *   - loading: Boolean indicating whether the mutation is in progress.
 *
 */

export const updateDuplicateSlideCache = (
  cache: ApolloCache,
  slide: SlideFieldsFragment,
  duplicationType: string | undefined
) => {
  // add new slide to target lesson cache
  cache.modify({
    fields: {
      slidesByLessonId(existingData, { storeFieldName }) {
        const key = `"lessonId":"${slide.lessonId}"`;
        const isTrashKey = '"isTrash":false';

        if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
          return existingData;

        if (!existingData?.items) return existingData;

        if (slide.isTrash) return existingData;

        const slideRef = { __ref: cache.identify(slide) };

        return {
          ...existingData,
          items: [slideRef, ...existingData.items],
          total: existingData.total + 1,
        };
      },
    },
  });

  if (duplicationType === "linked") {
    cache.modify({
      id: `Slide:${slide.originalSlideId}`,

      fields: {
        linkedCopies: (existingData) => {
          return [...existingData, { id: slide.id, name: slide.name }];
        },
      },
    });
  }
};

export function useDuplicateSlide() {
  const navigate = useNavigate();
  const [duplicateSlideMutation, { loading }] = useMutation(DuplicateSlideDocument, {
    update(cache, { data }, { variables }) {
      if (!data?.duplicateSlide) return;
      const slide = useFragment(SlideFieldsFragmentDoc, data.duplicateSlide);
      const duplicationType = variables?.duplicationType;

      try {
        updateDuplicateSlideCache(cache, slide, duplicationType);
      } catch (error) {
        console.error("Cache update failed:", error);
      }
    },
  });

  const duplicateSlide = async (
    slideId: string,
    targetLessonId: string,
    name: string,
    duplicationType: "unique" | "linked",
    _originalSlideId: string | null,
    insertAfterSortOrder?: number | null
  ) => {
    return withToast(
      async () => {
        const result = await duplicateSlideMutation({
          variables: {
            slideId,
            targetLessonId,
            name,
            duplicationType,
            insertAfterSortOrder,
          },
        });

        const maskedSlide = result.data?.duplicateSlide;
        const duplicatedSlide = maskedSlide
          ? useFragment(SlideFieldsFragmentDoc, maskedSlide)
          : undefined;

        // Navigate and scroll to the duplicated slide
        if (duplicatedSlide) {
          saveScrollTarget(`slide-${duplicatedSlide.id}`, targetLessonId);

          navigate({
            to: "/admin/lesson/$lessonId",
            params: { lessonId: targetLessonId },
          });
        }

        return duplicatedSlide;
      },
      {
        loading: "Duplicating slide...",
        success: `Successfully duplicated slide "${name}"`,
        error: "Failed to duplicate slide",
      },
      "duplicateSlide"
    );
  };

  return {
    duplicateSlide,
    loading,
  };
}
